import { Platform } from 'react-native';
import i18n from '../../../i18n';
import { sendPrinterAndroid } from '../../helpers/printerLibrary/printer';
import { loadStatisticsPrinters } from '../../helpers/statisticsPrinter';

/*
 * What is printed (NPIPE spec v1.1.6):
 *  - Wallee: result.receipts[] (MERCHANT + CLIENT, plain TEXT or HTML) is printed verbatim.
 *    The terminal text already contains everything the spec requires (DCC wording, the
 *    "Kereskedo alairasa" merchant signature line on refunds, "COPY" header on reprints).
 *    The "#pRCPT_HEADER_LOGO" marker is a logo placeholder: the thermal layer has no
 *    store-logo support, so the marker line is dropped.
 *  - Sca (SZEP/EP): request param printout 0 = no printout, 1 = printed by the SCA app on the
 *    terminal, 2 = printed by the host (this app) from the key/value lines of the response.
 *  - POSitive / EVO: terminalPrintingIndicator != 0 means the terminal already printed, so we
 *    print nothing. Otherwise (host must print) a minimal slip is built from the response fields.
 */

const LOGO_MARKER = '#pRCPT_HEADER_LOGO';
const TYPES = { MERCHANT: 'MERCHANT', CLIENT: 'CLIENT' };

// Same Hungarian codepage the app's existing TCP receipt path uses (oldPrinter.sendPrinter).
const TCP_CODEPAGE = { charset: 'Cp852', encodingId: 0 };
const BLUETOOTH_CODEPAGE = { charset: 'Cp852', encodingId: 6 };

const tr = (key, defaultValue) => i18n.t(key, { defaultValue });

const isTruthyIndicator = (value) => {
    if (typeof value === 'string') {
        return value !== '' && value !== '0' && value.toLowerCase() !== 'false';
    }
    return Boolean(value) && value !== 0;
};

const pick = (result, name) => {
    const candidates = [result?.[name], result?.raw?.responseParams?.[name], result?.raw?.[name]];
    return candidates.find((value) => value !== undefined && value !== null && value !== '');
};

const decodeEntities = (text) => text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, '&');

const htmlToText = (html) => decodeEntities(
    String(html)
        .replace(/<\s*br\s*\/?>/gi, '\n')
        .replace(/<\/\s*(p|div|h[1-6]|tr|li)\s*>/gi, '\n')
        .replace(/<[^>]+>/g, '')
);

/** Remove printer-markup control sequences so terminal text can never alter the layout. */
const sanitizeLine = (line) => String(line)
    .replace(/\r/g, '')
    .replace(/\[(L|C|R)\]/gi, '')
    .replace(/</g, '(')
    .replace(/>/g, ')')
    .replace(/\s+$/, '');

const textToLines = (content, format) => {
    const raw = String(format).toUpperCase() === 'HTML' ? htmlToText(content) : String(content);
    const lines = raw.replace(/\r\n?/g, '\n').split('\n')
        .filter((line) => !line.includes(LOGO_MARKER))
        .map(sanitizeLine);
    const out = [];
    lines.forEach((line) => {
        if (line === '' && (out.length === 0 || out[out.length - 1] === '')) {
            return;
        }
        out.push(line);
    });
    while (out.length && out[out.length - 1] === '') {
        out.pop();
    }
    return out;
};

/** SCA response lines come as [{KEY: 'v'}, ...] or as a plain map. */
const normalizeKeyValues = (printoutLines) => {
    const map = {};
    if (Array.isArray(printoutLines)) {
        printoutLines.forEach((entry) => {
            if (entry && typeof entry === 'object') {
                Object.keys(entry).forEach((key) => {
                    map[key] = entry[key];
                });
            }
        });
    } else if (printoutLines && typeof printoutLines === 'object') {
        Object.assign(map, printoutLines);
    }
    return map;
};

const numbered = (map, prefix) => Object.keys(map)
    .filter((key) => new RegExp(`^${prefix}\\d+$`).test(key))
    .sort((a, b) => Number(a.slice(prefix.length)) - Number(b.slice(prefix.length)))
    .map((key) => String(map[key]).trim())
    .filter(Boolean);

const row = (label, value) => (value === undefined || value === null || value === '' ? null : `${label}: ${value}`);

const compact = (lines) => lines.filter((line) => line !== null && line !== undefined);

const buildScaLines = (map, type) => {
    const isReversal = Boolean(map.REVTRANID);
    const amount = map.AMOUNT !== undefined ? `${map.AMOUNT} ${map.CURRENCYSYMBOL || ''}`.trim() : null;
    return compact([
        ...numbered(map, 'H'),
        '',
        isReversal ? tr('npipe_receipt_reversal', 'REVERSAL') : tr('npipe_receipt_sale', 'SALE'),
        type === TYPES.MERCHANT
            ? tr('npipe_receipt_merchant_copy', 'MERCHANT COPY')
            : tr('npipe_receipt_customer_copy', 'CUSTOMER COPY'),
        '',
        row('TID', map.TID),
        row('MID', map.MID),
        row('CATI', map.CATI),
        row(tr('npipe_receipt_card', 'Card'), map.CARDTYPE),
        row(tr('npipe_receipt_card_no', 'Card no.'), map.CARDNO),
        map.POCKETNAME ? row(tr('npipe_receipt_pocket', 'Pocket'), map.POCKETNAME) : null,
        row(tr('npipe_receipt_tran_id', 'Transaction ID'), map.TRANID),
        isReversal ? row(tr('npipe_receipt_reversal_id', 'Reversal ID'), map.REVTRANID) : null,
        row(tr('npipe_receipt_auth_code', 'Auth. code'), map.AUTHCODE),
        row(tr('npipe_receipt_date', 'Date'), map.DATETIME),
        amount ? row(tr('npipe_receipt_amount', 'Amount'), amount) : null,
        map.RESPMESSAGE ? String(map.RESPMESSAGE).trim() : null,
        '',
        ...numbered(map, 'F'),
    ]).map(sanitizeLine);
};

const formatMinor = (value, currency) => {
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return null;
    }
    return `${(n / 100).toFixed(2)} ${currency || ''}`.trim();
};

/** Minimal slip for POSitive/EVO when the terminal did not print (host app must). */
const buildHostSlipLines = (result, type) => {
    const currency = pick(result, 'currency');
    const dccCurrency = pick(result, 'terminalCurrency');
    const rate = pick(result, 'currencyExchangeRate');
    const gratuity = Number(pick(result, 'amountGratuity')) || 0;
    const isDcc = Boolean(dccCurrency || rate || pick(result, 'dccText1'));
    const date = [pick(result, 'transactionDate'), pick(result, 'transactionTime')].filter(Boolean).join(' ')
        || pick(result, 'transactionDateTime') || pick(result, 'transactionDateAndTime');
    return compact([
        pick(result, 'transactionTitle') ? String(pick(result, 'transactionTitle')) : null,
        type === TYPES.MERCHANT
            ? tr('npipe_receipt_merchant_copy', 'MERCHANT COPY')
            : tr('npipe_receipt_customer_copy', 'CUSTOMER COPY'),
        '',
        row('TID', pick(result, 'terminalId')),
        row('MID', pick(result, 'merchantReference')),
        row(tr('npipe_receipt_card', 'Card'), result.cardBrand || pick(result, 'cardBrandName')),
        row(tr('npipe_receipt_card_no', 'Card no.'), result.maskedCard || pick(result, 'maskedCardNumber')),
        row(tr('npipe_receipt_slip_no', 'Slip no.'), result.slipNumber ?? pick(result, 'slipNumber')),
        row(tr('npipe_receipt_ref_no', 'Ref. no.'), result.referenceNumber || pick(result, 'referenceNumber')),
        row(tr('npipe_receipt_auth_code', 'Auth. code'), result.authCode || pick(result, 'authorisationCode')),
        row(tr('npipe_receipt_date', 'Date'), date),
        '',
        row(tr('npipe_receipt_amount', 'Amount'), formatMinor(result.amount ?? pick(result, 'amount'), currency)),
        gratuity ? row(tr('npipe_receipt_tip', 'Tip'), formatMinor(gratuity, currency)) : null,
        isDcc && rate ? row(tr('npipe_receipt_exchange_rate', 'Exchange rate'), rate) : null,
        isDcc && dccCurrency ? row(tr('npipe_receipt_dcc_currency', 'Card currency'), dccCurrency) : null,
        isDcc && pick(result, 'dccText1') ? String(pick(result, 'dccText1')) : null,
        isDcc && pick(result, 'dccText2') ? String(pick(result, 'dccText2')) : null,
        type === TYPES.CLIENT && pick(result, 'aosa') ? row(tr('npipe_receipt_balance', 'Balance'), pick(result, 'aosa')) : null,
    ]).map(sanitizeLine);
};

const hasTerminalPrinted = (result) => isTruthyIndicator(
    pick(result, 'terminalPrintingIndicator') ?? pick(result, 'isTerminalPrinting') ?? result?.terminalPrinted
);

/**
 * Decide and build the receipts this app must print for a normalized NPIPE result.
 * Returns an empty array when nothing has to be printed (terminal printed, printout 0/1, declined
 * without slip text).
 * @param {object} result normalized npipeClient result
 * @param {{printout?: number, config?: {printout?: number}}} [opts] `printout` = SCA request param (0/1/2)
 * @returns {Array<{type: 'MERCHANT'|'CLIENT', lines: string[]}>}
 */
export const buildReceiptLines = (result, opts = {}) => {
    if (!result || typeof result !== 'object') {
        return [];
    }

    if (Array.isArray(result.receipts) && result.receipts.some((r) => r && String(r.content || '').trim())) {
        return result.receipts
            .filter((r) => r && String(r.content || '').trim())
            .map((r) => ({
                type: String(r.type || r.receiptType).toUpperCase() === TYPES.CLIENT ? TYPES.CLIENT : TYPES.MERCHANT,
                lines: textToLines(r.content, r.format || r.receiptFormat || 'TEXT'),
            }))
            .filter((r) => r.lines.length > 0);
    }

    const printout = opts.printout ?? opts.config?.printout;
    const map = normalizeKeyValues(result.printoutLines);
    if (Object.keys(map).length > 0) {
        if (printout !== undefined && printout !== null && Number(printout) !== 2) {
            return [];
        }
        return [TYPES.MERCHANT, TYPES.CLIENT].map((type) => ({ type, lines: buildScaLines(map, type) }));
    }

    if (result.approved && !hasTerminalPrinted(result) && result.amount !== undefined) {
        return [TYPES.MERCHANT, TYPES.CLIENT].map((type) => ({ type, lines: buildHostSlipLines(result, type) }));
    }
    return [];
};

const toPayload = (lines) => `${lines.map((line) => `[L]${line}`).join('\n')}\n\n\n`;

const withCodepage = (printer) => (printer?.ip
    ? { ...TCP_CODEPAGE, ...printer }
    : { ...BLUETOOTH_CODEPAGE, ...printer });

/**
 * Print the receipts of an NPIPE result through the app's receipt/cash-register printers.
 * @param {object} result normalized npipeClient result
 * @param {{copies?: number, which?: 'both'|'merchant'|'client', printout?: number, config?: object, printers?: object[]}} [opts]
 *   `copies` = copies of each receipt (default 1), `which` default 'both', `printers` overrides the configured printers.
 * @returns {Promise<boolean>} true when something was printed (or nothing needed printing), false on failure
 */
export const printNpipeReceipts = async (result, opts = {}) => {
    const receipts = buildReceiptLines(result, opts);
    const which = String(opts.which || 'both').toUpperCase();
    const selected = receipts.filter((r) => which === 'BOTH' || r.type === which);
    if (!selected.length) {
        return true;
    }
    if (Platform.OS !== 'android') {
        return false;
    }

    const printers = Array.isArray(opts.printers) ? opts.printers : await loadStatisticsPrinters();
    if (!printers.length) {
        return false;
    }

    const copies = Math.max(1, Math.floor(Number(opts.copies)) || 1);
    let printed = 0;
    for (const printer of printers) {
        let ok = true;
        for (const receipt of selected) {
            for (let i = 0; i < copies; i += 1) {
                const sent = await sendPrinterAndroid(toPayload(receipt.lines), withCodepage(printer), true, false);
                ok = ok && sent === true;
            }
        }
        if (ok) {
            printed += 1;
        }
    }
    return printed > 0;
};
